import React from 'react';
import classNames from 'classnames';
import { hintForError } from '../util/errorHints';
import './AlertBanner.scss';

/**
 * A page-level alert, at the top of the content where a page-level failure belongs.
 *
 * It exists because the pricing page rendered its checkout error INSIDE `.plan-cta`, between the
 * button and the feature list — a whole-page failure dressed as a property of one button, and it
 * shoved the list down 32px when it appeared. Anything a page needs to say about itself goes here
 * instead, in one consistent place.
 *
 * `cause` is a raw error message and is NEVER printed as-is: it goes through `hintForError`, which
 * renders a recognised cause and otherwise falls back to a generic line. That is what stops
 * `api_key cannot be the empty string` (and `Server Error` once APP_DEBUG is off) reaching a user.
 * Pass `message` instead when the copy is already written for a human.
 */
export default function AlertBanner({
  cause,
  message,
  tone = 'error',
  onDismiss
}: {
  cause?: string | null;
  message?: string | null;
  tone?: 'error' | 'info';
  onDismiss?: () => void;
}) {
  const hint = cause ? hintForError(cause) : null;
  const text = message ?? hint?.text ?? (cause ? 'Something went wrong. Please try again.' : null);

  if (!text) return null;

  return (
    <div
      className={ classNames('alert-banner', `alert-banner--${tone}`) }
      // `alert` announces immediately, which is right for something that interrupts a task.
      role="alert"
    >
      <span className="alert-banner-text">
        { text }
        { hint?.action?.href && (
          <>
            {' '}
            <a href={ hint.action.href }>{ hint.action.label }</a>
          </>
        ) }
      </span>
      { onDismiss && (
        <button
          type="button"
          className="alert-banner-dismiss"
          onClick={ onDismiss }
          aria-label="Dismiss"
        >
          ×
        </button>
      ) }
    </div>
  );
}
